import React, { useEffect, useState } from 'react';
import { useSessionStorage } from '@/hooks/useSessionStorage';
import { useParams, useNavigate } from 'react-router-dom';
import FullscreenMap, { Camera as MapCamera } from '@/components/maps/FullscreenMap';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { EnumArmProfile, CurrentArmState, createSite } from './siteApi';
import { Button } from '@/components/ui/button';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { CommandLoading } from 'cmdk';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CustomerDto, useSearchCustomersQuery } from '@/hooks/customerHooks';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Spinner } from '@/components/ui/spinner';
// Removing contacts hook to fix React hooks error
import {
  Clock,
  Timer,
  ShieldCheck,
  MapPin,
  Info,
  BrainCircuit,
  Building2,
  Camera,
  Shield,
} from 'lucide-react';
import SiteDevices from './components/SiteDevices';
import { Schedule } from './components/Schedule';
import { MonitoringCompanies } from './components/MonitoringCompanies';
import { SiteLog } from './components/SiteLog';
import { Switch } from '@/components/ui/switch';
import { SiteCalendar } from './components/SiteCalendar';
import { useToast } from '@/hooks/use-toast';
import { SiteContacts } from './components/SiteContacts';
import { useGetArmStatus, useSite, useUpdateNoThreat, useUpdateSite } from '@/hooks/siteHooks';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { EnumSirenProfile, EnumAiAutomationLevel } from './siteApi';
import { AddressSearch } from '@/components/maps/AddressSearch';
import { GeocodingResult, getTimezoneFromCoordinates } from '@/components/maps/mapbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import SiteLocationMap from '@/components/maps/SiteLocationMap';
import TimezonePicker from '@/components/ui/timezone-picker';
import ExtServiceConfig from './components/ExtServiceConfig';
import { useCustomer } from '@/features/Customers/hooks/useCustomer';

interface ArmState {
  noThreatDisposition: boolean;
  noThreatMinutes: number;
  selectedArmProfile: EnumArmProfile;
}

const siteFormSchema = z.object({
  customerId: z.string().min(1, 'Customer is required'),
  name: z.string().min(1, 'Site name is required'),
  timeZone: z.string().min(1, 'Timezone is required'),
  newMonitoringAlertsAfterSeconds: z.number().min(0).default(0),
  siteNotes: z.string().optional(),
  sirenProfile: z.nativeEnum(EnumSirenProfile),
  sirenCooldownSeconds: z.number().min(0),
  sirenProfilePerson: z.nativeEnum(EnumSirenProfile),
  sirenProfileVehicle: z.nativeEnum(EnumSirenProfile),
  sirensUnlinked: z.boolean(),
  aiAutomationLevel: z.nativeEnum(EnumAiAutomationLevel),
  aiPrimer: z.string().optional(),
  noThreatAiPrimer: z.string().optional(),
  guardResponseEnabled: z.boolean(),
  guardResponseDispatchCode: z.string().optional(),
  complianceEnabled: z.boolean(),
  address: z.object({
    addressLine1: z.string().optional(),
    addressLine2: z.string().optional(),
    city: z.string().optional(),
    region: z.string().optional(),
    postCode: z.string().optional(),
    country: z.string().optional(),
    lat: z.number().min(-90).max(90, 'Latitude must be between -90 and 90'),
    lon: z.number().min(-180).max(180, 'Longitude must be between -180 and 180'),
    timezone: z.string().optional(),
  }).refine((data) => {
    // Require either address information or valid coordinates
    const hasAddress = data.addressLine1 && data.addressLine1.trim().length > 0;
    const hasValidCoordinates = data.lat !== 0 && data.lon !== 0;
    return hasAddress || hasValidCoordinates;
  }, {
    message: "Location is required - please provide either an address or valid coordinates",
    path: ["addressLine1"], // Show error on addressLine1 field
  }),
});

type SiteFormValues = z.infer<typeof siteFormSchema>;

const SiteAddNew: React.FC = () => {
  const navigate = useNavigate();
  const { customerId } = useParams();

  let customerPreSelected = customerId !== undefined;

  // TODO Check if companyId Exists

  // We don't need searchParams anymore since we're not using URL parameters for tabs
  // const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [armState, setArmState] = useState<ArmState>({
    noThreatDisposition: false,
    noThreatMinutes: 0,
    selectedArmProfile: EnumArmProfile.Auto,
  });

  // State for camera management

  const [error, setError] = useState<string | null>(null);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  // Simple tab state management
  const [activeTab, setActiveTab] = useSessionStorage('site-add-tab', 'site');
  const [sectionStatuses, setSectionStatuses] = useState<
    Record<string, 'complete' | 'incomplete' | 'pending'>
  >({});

  // Removing contacts hook to fix React hooks error

  const { mutateAsync: updateSiteMutation, isPending } = useUpdateSite({
    onSuccess: () => {
      toast({
        title: 'Site Updated',
        description: 'Site settings have been updated successfully',
        variant: 'default',
      });
    },
    onError: () => {
      setError('Failed to update site settings');
    },
  });

  const updateNoThreatMutation = useUpdateNoThreat({
    onSuccess: (result) => {
      if (result.success) {
        toast({
          title: 'No Threat Disposition Updated',
          description: 'No Threat Disposition has been updated successfully',
          variant: 'default',
        });
      } else {
        setError(result.message || 'Failed to update no threat disposition');
      }
    },
    onError: () => {
      setError('Failed to update no threat disposition');
    },
  });

  const [locationType, setLocationType] = useState<'address' | 'coordinates'>('address');
  const [mapLat, setMapLat] = useState(-36.86008);
  const [mapLon, setMapLon] = useState(174.760373);
  const [addressIsValid, setAddressIsValid] = useState(true);

  const [queryText, setQueryText] = useState('');
  const [selectorOpen, setSelectorOpen] = useState(false);

  const [preSelectedCustomerName, setPreSelectedCustomerName] = useState('');

  const { data: customersListed, status: customersStatus } = useSearchCustomersQuery({
    query: queryText,
    enabled: selectorOpen,
  });

  const {
    data: customerData,
    isLoading: isLoadingCustomer,
    error: customerError,
  } = useCustomer(customerId || '');

  const form = useForm<SiteFormValues>({
    resolver: zodResolver(siteFormSchema),
    defaultValues: {
      customerId: customerPreSelected ? customerId : '',
      name: '',
      timeZone: '',
      newMonitoringAlertsAfterSeconds: 1800,
      siteNotes: '',
      sirenProfile: EnumSirenProfile.None,
      sirenCooldownSeconds: 0,
      sirenProfilePerson: EnumSirenProfile.None,
      sirenProfileVehicle: EnumSirenProfile.None,
      sirensUnlinked: false,
      aiAutomationLevel: EnumAiAutomationLevel.None,
      aiPrimer: '',
      noThreatAiPrimer: '',
      guardResponseEnabled: false,
      guardResponseDispatchCode: '',
      complianceEnabled: false,
      address: {
        addressLine1: '',
        addressLine2: '',
        city: '',
        region: '',
        postCode: '',
        country: '',
        lat: 0,
        lon: 0,
        timezone: '',
      },
    },
  });

  const handleConfirmNavigation = () => {
    setShowUnsavedDialog(false);
    if (pendingNavigation) {
      setPendingNavigation(null);
    }
  };

  const handleCancelNavigation = () => {
    setShowUnsavedDialog(false);
    setPendingNavigation(null);
  };

  //   const handleArmProfileUpdate = (armState: ArmState) => {
  //     if (!siteId || !companyId || !site) return;
  //     updateSiteMutation({
  //       customerId: companyId,
  //       projectId: siteId,
  //       data: {
  //         name: site.name,
  //         onSightLogicGroupId: site.onSightLogicGroupId,
  //         newMonitoringAlertsAfterSeconds: site.newMonitoringAlertsAfterSeconds,
  //         sirenProfile: site.sirenProfile,
  //         armProfile: armState.selectedArmProfile,
  //         siteNotes: site.siteNotes,
  //         guardResponseEnabled: site.guardResponseEnabled,
  //         sirenCooldownSeconds: site.sirenCooldownSeconds,
  //         sirensUnlinked: site.sirensUnlinked,
  //         complianceEnabled: site.complianceEnabled,
  //         guardResponseDispatchCode: site.guardResponseDispatchCode,
  //         sirenProfilePerson: site.sirenProfilePerson,
  //         sirenProfileVehicle: site.sirenProfileVehicle,
  //         timeZone: site.timeZone,
  //         address: site.address,
  //         aiPrimer: site.aiPrimer,
  //         noThreatAiPrimer: site.noThreatAiPrimer,
  //         detectionLabels: site.detectionLabels,
  //         aiAutomationLevel: site.aiAutomationLevel,
  //       },
  //     });
  //   };

  //   const handleNoThreatUpdate = (minutes: number) => {
  //     if (!siteId || !companyId) return;
  //     updateNoThreatMutation.mutate({
  //       companyId,
  //       siteId,
  //       disarmTime: minutes,
  //     });
  //     setArmState({
  //       ...armState,
  //       noThreatDisposition: armState.noThreatMinutes >= 0,
  //       noThreatMinutes: minutes,
  //     });
  //   };

  //   const handleNoThreatToggle = () => {
  //     if (!siteId || !companyId) return;

  //     if (armState.noThreatDisposition) {
  //       handleNoThreatUpdate(-1);
  //     } else {
  //       handleNoThreatUpdate(armState.noThreatMinutes);
  //     }

  //     setArmState({ ...armState, noThreatDisposition: !armState.noThreatDisposition });
  //   };

  //   const getArmedColor = () => {
  //     if (armStatus?.status === CurrentArmState.Armed) return 'bg-green-800';
  //     if (armStatus?.status === CurrentArmState.TestMode) return 'bg-orange-800';
  //     return 'bg-red-700';
  //   };

  //   const getArmedText = () => {
  //     if (armStatus?.status === CurrentArmState.Armed) return 'System Armed';
  //     if (armStatus?.status === CurrentArmState.TestMode) return 'Test Mode';
  //     return 'Disarmed';
  //   };

  const onSubmit = async (data: SiteFormValues) => {
    // TODO Check customer Id and site name
    if (!data.name) {
      console.error('Site name required');
      return;
    }

    if (data.customerId === '') {
      console.error('Customer ID required');
      return;
    }

    // Check address validation when using address location type
    if (locationType === 'address' && !addressIsValid) {
      toast({
        variant: 'destructive',
        title: 'Invalid Address',
        description: 'Please select an address from the suggestions.',
      });
      return;
    }

    try {
      // Create the new site
      const result = await createSite(data.customerId!, {
        name: data.name,
      });

      // Update the basic site information
      const customerId = data.customerId;
      const projectId = result.project!.id;

      if (!projectId) {
        throw new Error('No project ID');
      }

      await updateSiteMutation({
        customerId: customerId!,
        projectId: projectId!,
        data,
      });

      // Navigate to the new site
      navigate(`/Customer/${customerId}/project/${projectId}`);
    } catch (error) {
      console.error('Failed to create site:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to create site. Please try again.',
      });
    }
  };

  if (error) {
    return (
      <div className="flex h-full flex-col items-center justify-center">
        <div className="rounded-lg bg-red-500/10 p-6 text-center">
          <h2 className="mb-2 text-xl font-semibold text-red-500">Error Loading Site</h2>
          <p className="mb-4 text-gray-300">{error}</p>
          <div className="flex justify-center gap-2">
            <button
              onClick={() => navigate('/sites')}
              className="rounded bg-gray-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-gray-700"
            >
              Return to Sites
            </button>
            <button
              onClick={() => window.location.reload()}
              className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  const getSectionStatus = (section: string) => {
    return sectionStatuses[section] || 'incomplete';
  };

  const renderStatusIcon = (status: string) => {
    switch (status) {
      case 'complete':
        return <div className="mr-2 h-2 w-2 rounded-full bg-green-500" />;
      case 'incomplete':
        return <div className="mr-2 h-2 w-2 rounded-full bg-gray-500" />;
      case 'issues':
        return <div className="mr-2 h-2 w-2 rounded-full bg-orange-500" />;
      default:
        return <div className="mr-2 h-2 w-2 rounded-full bg-gray-500" />;
    }
  };

  const aiAutomationLevel = form.watch('aiAutomationLevel');

  return (
    <>
      <Dialog open={showUnsavedDialog} onOpenChange={setShowUnsavedDialog}>
        <DialogContent className="p-6">
          <DialogHeader>
            <DialogTitle>Unsaved Changes</DialogTitle>
          </DialogHeader>
          <DialogDescription>
            You have unsaved changes to the arm profile. Are you sure you want to leave? Your
            changes will be lost.
          </DialogDescription>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancelNavigation}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleConfirmNavigation}>
              Leave Without Saving
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    
      <div className="mx-auto">
        <section className="mx-auto grid max-w-[1200px] mt-24 gap-8 grid-cols-1 md:grid-cols-2 ">
          <div className='flex items-start gap-4 mt-8 flex-col mx-4 md:mx-0'>   <div className="flex items-center gap-4 ">
          <Building2 className="h-6 w-6" /> <h1 className='text-xl font-semibold' >Add a new site</h1>   </div>
          <p className=" text-muted-foreground">Sites help you manage security at a location by organizing devices, contacts, and settings at your site in a single place. Continue by entering some details about your site.</p>
     
      
          </div>
          <Form {...form}>
            <form id="site-form" onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 ">
              <div className="space-y-6">
                <div className=" grid  gap-6">
                  <div className="overflow-hidden rounded-lg border bg-[#111] text-white">
                    {customerPreSelected && (
                      <div className='p-4 border-b font-semibold'>
                        <p>Adding site to {customerData?.name}</p>
                      </div>
                    )}
                    {!customerPreSelected && (
                      <FormField
                        control={form.control}
                        name="customerId"
                        render={({ field }) => (
                          <FormItem className="m-4" data-onboarding="site-add-customer-select">
                            <FormLabel>Customer</FormLabel>
                            <FormControl>
                              <Popover
                                open={selectorOpen}
                                onOpenChange={(change) => {
                                  setSelectorOpen(change);
                                  if (change) {
                                    setQueryText('');
                                  }
                                }}
                              >
                                <PopoverTrigger asChild>
                                  <Button
                                    variant="outline"
                                    role="combobox"
                                    aria-expanded={selectorOpen}
                                    className="w-full justify-between text-white"
                                    
                                  >
                                    {field.value && customersListed
                                      ? customersListed.pages
                                          .flat()
                                          .find((customer) => customer.id === field.value)?.name ||
                                        'Select Customer...'
                                      : 'Select Customer...'}
                                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent
                                  className="w-[--radix-popover-trigger-width] p-0"
                                  align="start"
                                >
                                  <Command filter={() => 1}>
                                    <CommandInput
                                      placeholder="Search customers..."
                                      className="h-9"
                                      onValueChange={setQueryText}
                                    />
                                    <CommandList>
                                      <CommandEmpty>
                                        {customersStatus === 'success' && 'No customer found.'}
                                      </CommandEmpty>
                                      <CommandGroup>
                                        {customersListed?.pages
                                          .flat()
                                          .map((customer: CustomerDto) => (
                                            <CommandItem
                                              key={customer.id}
                                              value={customer.id}
                                              onSelect={(currentValue) => {
                                                field.onChange(
                                                  currentValue === field.value ? '' : currentValue
                                                );
                                                setSelectorOpen(false);
                                              }}
                                            >
                                              {customer.name}
                                              <Check
                                                className={cn(
                                                  'ml-auto',
                                                  field.value === customer.id
                                                    ? 'opacity-100'
                                                    : 'opacity-0'
                                                )}
                                              />
                                            </CommandItem>
                                          ))}
                                      </CommandGroup>
                                      {customersStatus === 'pending' && (
                                        <CommandLoading>
                                          <div className="flex items-center justify-center p-2">
                                            <Spinner size="small" />
                                          </div>
                                        </CommandLoading>
                                      )}
                                    </CommandList>
                                  </Command>
                                </PopoverContent>
                              </Popover>
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    )}
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem className="m-4" data-onboarding="site-add-name">
                          <FormLabel>Site Name</FormLabel>
                          <FormControl>
                            <Input {...field}  />
                          </FormControl>
                        </FormItem>
                        
                      )}
                    />   
                    <FormField
                      control={form.control}
                      name="address"
                      render={({ fieldState }) => (
                        <FormItem className="m-4" data-onboarding="site-add-address">
                          <FormLabel>Location</FormLabel>
                          <FormControl>
                            <div className="space-y-4">
                              <RadioGroup
                                value={locationType}
                                onValueChange={(value: 'address' | 'coordinates') => setLocationType(value)}
                                className="mb-4 flex gap-4"
                              >
                                <div className="flex items-center space-x-2">
                                  <RadioGroupItem value="address" id="address" />
                                  <Label htmlFor="address">Address</Label>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <RadioGroupItem value="coordinates" id="coordinates" />
                                  <Label htmlFor="coordinates">Coordinates</Label>
                                </div>
                              </RadioGroup>
                              
                              <div className={`space-y-4 ${locationType === 'address' ? '' : 'hidden'}`}>
                                <AddressSearch
                                  onSelect={(result: GeocodingResult) => {
                                    form.setValue('address.addressLine1', result.address || '');
                                    form.setValue('address.addressLine2', result.address_line2 || '');
                                    form.setValue('address.city', result.city || '');
                                    form.setValue('address.region', result.state || '');
                                    form.setValue('address.postCode', result.postcode || '');
                                    form.setValue('address.country', result.country || '');
                                    form.setValue('address.lat', result.center[1]);
                                    form.setValue('address.lon', result.center[0]);
                                    setMapLat(result.center[1]);
                                    setMapLon(result.center[0]);
                                    
                                    // Auto-detect and set timezone based on coordinates
                                    const timezone = getTimezoneFromCoordinates(result.center[0], result.center[1]);
                                    if (timezone) {
                                      form.setValue('timeZone', timezone);
                                    }
                                  }}
                                  onValidationChange={(isValid) => setAddressIsValid(isValid)}
                                  placeholder="Search for an address"
                                />
                              </div>
                              
                              <div className={`space-y-4 ${locationType === 'coordinates' ? '' : 'hidden'}`}>
                                <div className="grid grid-cols-2 gap-4">
                                  <FormField
                                    control={form.control}
                                    name="address.lat"
                                    render={({ field }) => (
                                      <FormItem>
                                        <FormLabel>Latitude</FormLabel>
                                        <FormControl>
                                          <Input
                                            type="number"
                                            step="any"
                                            {...field}
                                            onChange={(e) => {
                                              const value = parseFloat(e.target.value) || 0;
                                              field.onChange(value);
                                              setMapLat(value);
                                              
                                              // Auto-detect timezone when both lat and lon are available
                                              const currentLon = form.getValues('address.lon');
                                              if (value && currentLon) {
                                                const timezone = getTimezoneFromCoordinates(currentLon, value);
                                                if (timezone) {
                                                  form.setValue('timeZone', timezone);
                                                }
                                              }
                                            }}
                                          />
                                        </FormControl>
                                      </FormItem>
                                    )}
                                  />
                                  <FormField
                                    control={form.control}
                                    name="address.lon"
                                    render={({ field }) => (
                                      <FormItem>
                                        <FormLabel>Longitude</FormLabel>
                                        <FormControl>
                                          <Input
                                            type="number"
                                            step="any"
                                            {...field}
                                            onChange={(e) => {
                                              const value = parseFloat(e.target.value) || 0;
                                              field.onChange(value);
                                              setMapLon(value);
                                              
                                              // Auto-detect timezone when both lat and lon are available
                                              const currentLat = form.getValues('address.lat');
                                              if (value && currentLat) {
                                                const timezone = getTimezoneFromCoordinates(value, currentLat);
                                                if (timezone) {
                                                  form.setValue('timeZone', timezone);
                                                }
                                              }
                                            }}
                                          />
                                        </FormControl>
                                      </FormItem>
                                    )}
                                  />
                                </div>
                              </div>
                              
                              <div className="mt-4">
                                <SiteLocationMap
                                  coordinates={{
                                    lat: mapLat,
                                    lng: mapLon,
                                  }}
                                  onCoordinatesChange={(coords) => {
                                    form.setValue('address.lat', coords.lat);
                                    form.setValue('address.lon', coords.lng);
                                    setMapLat(coords.lat);
                                    setMapLon(coords.lng);
                                    
                                    // Auto-detect timezone when coordinates change via map interaction
                                    const timezone = getTimezoneFromCoordinates(coords.lng, coords.lat);
                                    if (timezone) {
                                      form.setValue('timeZone', timezone);
                                    }
                                  }}
                                  onAddressChange={(address, lat, lng) => {
                                    form.setValue('address.addressLine1', address);
                                    form.setValue('address.lat', lat);
                                    form.setValue('address.lon', lng);
                                    setMapLat(lat);
                                    setMapLon(lng);
                                    
                                    // Auto-detect timezone when address changes via map interaction
                                    const timezone = getTimezoneFromCoordinates(lng, lat);
                                    if (timezone) {
                                      form.setValue('timeZone', timezone);
                                    }
                                  }}
                                />
                              </div>
                            </div>
                          </FormControl>
                          {fieldState.error?.message && <FormMessage />}
                        </FormItem>
                      )}
                    />
                    {/* <FormField
                      control={form.control}
                      name="timeZone"
                      render={({ field }) => (
                        <FormItem className=" m-4" data-onboarding="site-add-timezone">
                          <FormLabel>Time Zone</FormLabel>
                          <FormControl>
                            <TimezonePicker field={field} form={form} />
                          </FormControl>
                        </FormItem>
                      )}
                    />  */}
                    <div className="p-4 border-t">
                    <Button
            type="submit"
            form="site-form"
            disabled={isPending}
            className="bg-lime text-black hover:bg-lime/90 w-full"
          >
            {isPending ? 'Processing...' : 'Continue '}
          </Button>
                    </div>
                  
                  </div>
          
                </div>


              </div>
            </form>
          </Form>
        </section>
      </div>
    </>
  );
};

export default SiteAddNew;
